import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useError } from "../../context/errorContext";
import SignatureCanvas from "react-signature-canvas";
import ReplayCircleFilledRoundedIcon from '@mui/icons-material/ReplayCircleFilledRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ViewInArRoundedIcon from '@mui/icons-material/ViewInArRounded';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import QueryBuilderRoundedIcon from '@mui/icons-material/QueryBuilderRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import axiosInstance from "../../api/axiosInstance";
import useWakeLock from "../../hooks/screenWakeLock";
import moment from "moment/moment";
import "./sign.scss";

const Sign = () => {
    const [doc, setDoc] = useState(null);

    const sigCanvas = useRef(null);
    const wakeLockActive = useWakeLock();

    const { showMessage } = useError();

    useEffect(() => {
        const fetchDocument = async () => {
            try {

                const res = await axiosInstance.get("/sign/doc");

                setDoc(res.data)

            } catch (err) {
                console.error(err);
            }
        };

        fetchDocument();

        const interval = setInterval(fetchDocument, 5000);

        return () => clearInterval(interval);
    }, []);

    const clearSignature = () => {
        sigCanvas.current.clear();
    };

    const signDoc = async () => {
        if (!doc) {
            return;
        }

        if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
            showMessage("Bitte unterschreiben Sie zuerst!");
            return;
        }

        try {
            const signature = sigCanvas.current.toDataURL("image/png");

            await axiosInstance.post("/sign/doc", {
                serialNr: doc.WEIGHTSERIALNR,
                docNr: doc.WEIGHTID,
                signature
            });

            showMessage("Lieferschein erfolgreich unterschrieben!");

            sigCanvas.current.clear();
            setDoc(null);

        } catch (error) {
            if (error.response?.status === 409) {
                showMessage("Dieser Lieferschein wurde bereits unterschrieben!");
                setDoc(null);
            } else {
                console.error("Fehler beim Speichern der Unterschrift:", error);
                showMessage("Die Unterschrift konnte nicht gespeichert werden!");
            }
        }
    };

    return (
        <div className="sign">
            <Helmet>
                <title>Sign</title>
            </Helmet>
            {doc ? (
                <div className="viewDocument">
                    <div className="document">
                        <div className="heading">
                            <TextSnippetOutlinedIcon className="icon" />
                            <div className="right">
                                <h4>Lieferschein {doc.WEIGHTSERIALNR}/{doc.WEIGHTID}</h4>
                                <span>Zum Unterschreiben</span>
                            </div>
                        </div>
                        <div className="grid">
                            <div className="layout">
                                <div className="group">
                                    <PersonRoundedIcon />
                                    <div className="data">
                                        <span className="label">Kunde</span>
                                        <span className="value">{doc.COMPANY1}</span>
                                    </div>
                                </div>
                                <div className="group">
                                    <LocalShippingRoundedIcon />
                                    <div className="data">
                                        <span className="label">Fahrzeug</span>
                                        <span className="value">{doc.LORRYREGIST}</span>
                                    </div>
                                </div>
                                <div className="group">
                                    <QueryBuilderRoundedIcon />
                                    <div className="data">
                                        <span className="label">Transportbeginn</span>
                                        <span className="value">{moment(doc.UPDATED).format("L [um] hh:mm [Uhr]")}</span>
                                    </div>
                                </div>
                                <div className="group">
                                    <LocationOnRoundedIcon />
                                    <div className="data">
                                        <span className="label">Bestimmungsort</span>
                                        <p className="value">
                                            {doc.DESTNAME1} {doc.DESTNAME2}<br />
                                            {doc.ZIP}, {doc.PLACE}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="grid">
                            <div className="heading">
                                <ViewInArRoundedIcon />
                                <h4>Material und Menge</h4>
                            </div>
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: "150px" }}>Artikel Kodex</th>
                                        <th>Beschreibung</th>
                                        <th style={{ width: "40px" }}>VE</th>
                                        <th style={{ width: "150px" }}>Stück ME</th>
                                        <th style={{ width: "150px" }}>Menge</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{doc.ARTICLEID}</td>
                                        <td>{doc.ARTICLEDESCRIPTION}</td>
                                        <td></td>
                                        <td>{doc.UNIT}</td>
                                        <td>{doc.QUANTITY.toLocaleString("de-DE")}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="grid">
                            <div className="heading">
                                <EditOutlinedIcon />
                                <h4>Unterschrift</h4>
                            </div>
                            <div className="signBox">
                                <div className="box">
                                    <SignatureCanvas
                                        ref={sigCanvas}
                                        penColor="black"
                                        canvasProps={{ className: "sigCanvas", width: 550, height: 200 }}
                                    />
                                    <div className="signLine" />
                                </div>
                            </div>
                            <div className="signControls">
                                <button className="light" onClick={() => setDoc(null)}>Abbrechen</button>
                                <div className="right">
                                    <button className="light" onClick={clearSignature}>
                                        <ReplayCircleFilledRoundedIcon />
                                        Zurücksetzen
                                    </button>
                                    <button onClick={signDoc}>
                                        <CheckRoundedIcon />
                                        Bestätigen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div >
            ) : (
                <div className="noDoc">
                    <h3>Kein Dokument zum unterschreiben</h3>
                </div>
            )
            }
        </div >
    );
};

export default Sign;